import {
  HTTP_HEADER_CLIENT_CHANNEL,
  STATUS_CODE,
  type ClientChannel,
} from '@/constants';
import { AuthProviderType } from '@/enums/auth-provider-type.enum';
import { ApiResponse, postFetcher } from './base-api';
import type {
  AuthLoginResponseData,
  AuthRefreshResponseData,
  PostAuthLoginParams,
  PostAuthPhonePinParams,
  PostAuthSocialRequest,
  PostAuthSocialResponseData,
} from './types/auth.type';

/** 인증번호 요청 (SMS 발송) - 회원가입 */
export const postAuthPhonePin = async (
  params: PostAuthPhonePinParams,
): Promise<ApiResponse<PostAuthPhonePinParams>> => {
  const digits = params.phone.replace(/\D/g, '');
  if (digits.length < 6) {
    return {
      code: 400,
      message: '올바른 인증번호를 입력해주세요.',
      data: params,
    };
  }
  return postFetcher<PostAuthPhonePinParams>('/auth/phone-pin', params);
};

/** POST /v1/auth/login - 로그인 */
export const postAuthLogin = async (
  body: PostAuthLoginParams,
  clientChannel?: ClientChannel,
): Promise<ApiResponse<AuthLoginResponseData>> => {
  return postFetcher<AuthLoginResponseData>('/auth/login', body, {
    skipAuth: true,
    skipAuthRefreshRetry: true,
    // 'app' 일 때만 refreshToken 이 쿠키가 아니라 body 로 온다 — 네이티브에 넘겨야 하므로
    // 앱 웹뷰 가입 직후에는 이 채널로 부른다.
    ...(clientChannel
      ? { headers: { [HTTP_HEADER_CLIENT_CHANNEL]: clientChannel } }
      : {}),
  });
};

/** POST /v1/auth/social - 소셜 로그인 (네이티브 SDK 토큰 기반) */
export const postAuthSocial = async (
  body: PostAuthSocialRequest,
  clientChannel: ClientChannel,
): Promise<ApiResponse<PostAuthSocialResponseData>> => {
  if (clientChannel === 'web' && body.provider === AuthProviderType.APPLE) {
    return {
      code: STATUS_CODE.BAD_REQUEST,
      message: 'Apple 소셜 로그인은 앱(WebView)에서만 이용할 수 있습니다.',
      data: {
        accessToken: '',
        nextStep: '',
        registered: false,
      },
    };
  }
  return postFetcher<PostAuthSocialResponseData>('/auth/social', body, {
    skipAuth: true,
    skipAuthRefreshRetry: true,
    headers: {
      [HTTP_HEADER_CLIENT_CHANNEL]: clientChannel,
    },
  });
};

/** POST /v1/auth/refresh-token - 토큰 갱신. refreshToken은 HttpOnly 쿠키로 전달 */
export async function postAuthRefreshToken(): Promise<
  ApiResponse<AuthRefreshResponseData>
> {
  return postFetcher<AuthRefreshResponseData>(
    '/auth/refresh-token',
    {},
    {
      skipAuth: true,
      skipAuthRefreshRetry: true,
    },
  );
}

/** 로그아웃. Authorization 없음 — refresh-token은 HttpOnly 쿠키만 전달 (refresh-token API와 동일) */
export async function logoutApi(): Promise<ApiResponse<unknown>> {
  try {
    return await postFetcher<unknown>(
      '/auth/logout',
      {},
      {
        skipAuth: true,
        skipAuthRefreshRetry: true,
      },
    );
  } catch {
    // 네트워크 오류 등으로 실패해도 클라이언트에서는 로그아웃 처리
    return { code: STATUS_CODE.OK, message: 'OK', data: {} };
  }
}
